import React, { useEffect, useState, useMemo } from "react";
import { View } from "@aws-amplify/ui-react";
import Pagination from "@mui/material/Pagination";
import { 
  TextField, 
  InputAdornment, 
  CircularProgress, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { getAllUsers, GetAllUsersParams, setUserAlwaysWinStatus, UserResponse } from "../../Common/SharedApis/Auth";
import "./User.css";
import BalancePopup from "./BalancePopup";

interface User extends UserResponse {
  // Extended interface for local state management
}

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [updatingWinStatus, setUpdatingWinStatus] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'deposit' | 'withdraw'>('deposit');
  const [popupOpen, setPopupOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');

  const navigate = useNavigate();

  // Memoized filtered users
  const filteredUsers = useMemo(() => {
    let filtered = users;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.activeBalance : !user.activeBalance
      );
    }

    if (roleFilter !== 'all' && roleFilter !== '') {
      filtered = filtered.filter(user => 
        user.roles?.includes(roleFilter)
      );
    }

    if (countryFilter !== 'all' && countryFilter !== '') {
      filtered = filtered.filter(user => 
        user.countryName === countryFilter
      );
    }

    return filtered;
  }, [users, statusFilter, roleFilter, countryFilter]);

  // Get unique values for filters
  const uniqueRoles = useMemo(() => {
    const roles = users.flatMap(user => user.roles?.split(', ') || []).filter(Boolean);
    return [...new Set(roles)];
  }, [users]);

  const uniqueCountries = useMemo(() => {
    const countries = users.map(user => user.countryName).filter(Boolean);
    return [...new Set(countries)];
  }, [users]);

  const fetchUsers = async (page: number, searchTerm: string) => {
    setLoading(true);
    try {
      const params: GetAllUsersParams = {
        IsActive: true,
        CountryId: "",
        IsEmailConfirmed: false,
        Role: "",
        SearchTerm: searchTerm,
        pageNumber: page,
        pageSize: pageSize,
      };

      const response = await getAllUsers(params);
      setUsers(response.users);
      setTotalUsers(response.totalUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (userId: string, actionType: string) => {
    if (actionType === 'Set Always Win') {
      const user = users.find(u => u.id === userId);
      if (user) {
        const newIsAlwaysWin = !user.isAlwaysWin;
        setUpdatingWinStatus(userId);
        try {
          await setUserAlwaysWinStatus(userId, newIsAlwaysWin);
          
          setUsers(prevUsers =>
            prevUsers.map(u =>
              u.id === userId ? { ...u, isAlwaysWin: newIsAlwaysWin } : u
            )
          );

          showSnackbar(`User always win status updated to ${newIsAlwaysWin ? 'Always Win' : 'Not Always Win'}`, 'success');
        } catch (error) {
          console.error('Failed to update always win status:', error);
          showSnackbar('Failed to update always win status', 'error');
        } finally {
          setUpdatingWinStatus(null);
        }
      }
    }
  };

  const handleOpenPopup = (userId: string, action: 'deposit' | 'withdraw') => {
    setSelectedUserId(userId);
    setActionType(action);
    setPopupOpen(true);
  };

  const updateUserBalance = (userId: string, newBalance: number) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId ? { ...user, avaliableBalance: newBalance } : user
      )
    );
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    fetchUsers(page, searchTerm);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setRoleFilter('all');
    setCountryFilter('all');
  };

  useEffect(() => {
    fetchUsers(page, searchTerm);
  }, [page, searchTerm]);

  const totalPages = Math.ceil(totalUsers / pageSize);
  
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  return (
    <View className="user-list-container">
      {/* Header Section */}
      <Box className="user-list-header">
        <Box className="header-content">
          <Typography variant="h4" component="h1" className="page-title">
            User Management
          </Typography>
          <Typography variant="body1" color="textSecondary" className="page-subtitle">
            Manage user accounts, balances, and permissions
          </Typography>
        </Box>
        
        <Box className="header-actions">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            className="refresh-btn"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Search and Filters Section */}
      <Card className="search-filters-card" elevation={0}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search users by name, email, ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                className="search-field"
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {uniqueRoles.map((role) => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Country</InputLabel>
                <Select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  label="Country"
                >
                  <MenuItem value="all">All Countries</MenuItem>
                  {uniqueCountries.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleClearFilters}
                fullWidth
                className="clear-filters-btn"
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      {loading ? (
        <Box className="loading-container">
          <CircularProgress size={60} />
          <Typography variant="h6" className="loading-text">
            Loading users...
          </Typography>
        </Box>
      ) : (
        <>
          <Card className="users-table-card" elevation={0}>
            <TableContainer>
              <Table aria-label="users table" className="users-table">
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell className="header-cell">User Information</TableCell>
                    <TableCell className="header-cell">Contact Details</TableCell>
                    <TableCell className="header-cell">Balance</TableCell>
                    <TableCell className="header-cell">Status & Roles</TableCell>
                    <TableCell className="header-cell" align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className="user-row" hover>
                      <TableCell className="user-info-cell">
                        <Box className="user-info">
                          <Avatar className="user-avatar">
                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                          </Avatar>
                          <Box className="user-details">
                            <Typography variant="subtitle1" className="user-name">
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="body2" className="user-username">
                              @{user.userName}
                            </Typography>
                            <Typography variant="caption" className="user-id">
                              ID: {user.customUserId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell className="contact-cell">
                        <Typography className="user-email">{user.email}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {user.phoneNumber || 'No phone number'}
                        </Typography>
                        {user.countryName && (
                          <Chip 
                            label={user.countryName} 
                            size="small" 
                            variant="outlined"
                            className="country-chip"
                          />
                        )}
                      </TableCell>
                      
                      <TableCell className="balance-cell">
                        <Box className="balance-info">
                          <Typography className="available-balance">
                            ${user.avaliableBalance.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Available Balance
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Locked: ${user.lockingBalance.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell className="status-cell">
                        <Box className="status-info">
                          <Chip
                            label={getStatusText(user.activeBalance)}
                            color={getStatusColor(user.activeBalance)}
                            size="small"
                            className="status-chip"
                          />
                          {user.roles && (
                            <Box className="roles-container">
                              {user.roles.split(', ').map((role, index) => (
                                <Chip
                                  key={index}
                                  label={role}
                                  size="small"
                                  variant="outlined"
                                  className="role-chip"
                                />
                              ))}
                            </Box>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            Last updated: {formatDate(user.lastModify)}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center" className="actions-cell">
                        <Box className="actions-container">
                          <Tooltip title="Deposit funds">
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleOpenPopup(user.id, 'deposit')}
                              className="action-btn deposit-btn"
                            >
                              Deposit
                            </Button>
                          </Tooltip>
                          
                          <Tooltip title="Withdraw funds">
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              onClick={() => handleOpenPopup(user.id, 'withdraw')}
                              className="action-btn withdraw-btn"
                            >
                              Withdraw
                            </Button>
                          </Tooltip>
                          
                          <Tooltip title={user.isAlwaysWin ? "Remove Always Win" : "Set Always Win"}>
                            <Button
                              variant="contained"
                              color={user.isAlwaysWin ? "success" : "error"}
                              size="small"
                              onClick={() => handleAction(user.id, 'Set Always Win')}
                              disabled={updatingWinStatus === user.id}
                              className="action-btn win-status-btn"
                            >
                              {updatingWinStatus === user.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                user.isAlwaysWin ? "Always Win" : "Not Always Win"
                              )}
                            </Button>
                          </Tooltip>
                          
                          <Tooltip title="View user details">
                            <Button
                              variant="outlined"
                              color="info"
                              size="small"
                              onClick={() => navigate(`/users/${user.id}`)}
                              className="action-btn details-btn"
                            >
                              Details
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Pagination and Results Summary */}
          <Box className="pagination-container">
            <Typography variant="body1" className="results-summary">
              Showing {filteredUsers.length} of {totalUsers} users
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              showFirstButton
              showLastButton
              className="pagination"
            />
          </Box>
        </>
      )}

      {/* Balance Popup */}
      {selectedUserId && (
        <BalancePopup
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          userId={selectedUserId}
          action={actionType}
          onBalanceUpdate={updateUserBalance}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </View>
  );
};

export default UserList;